/**
 * Thin wrapper around Apple's AppleID.auth global.
 *
 * The script tag in index.html loads appleid.auth.js. We init once on
 * app mount with the Services ID from Vite env, then call
 * AppleID.auth.signIn() from the button click handler.
 *
 * https://developer.apple.com/documentation/sign_in_with_apple/sign_in_with_apple_js
 */

interface AppleAuthConfig {
  clientId: string;
  scope: string;
  redirectURI: string;
  usePopup: boolean;
}

interface AppleAuthResponse {
  authorization: {
    code: string;
    id_token: string;
    state?: string;
  };
  user?: {
    email?: string;
    name?: {
      firstName?: string;
      lastName?: string;
    };
  };
}

declare global {
  interface Window {
    AppleID?: {
      auth: {
        init: (config: AppleAuthConfig) => void;
        signIn: () => Promise<AppleAuthResponse>;
      };
    };
  }
}

let initialized = false;

export const initAppleSignIn = () => {
  if (initialized) return;
  const serviceId = import.meta.env.VITE_APPLE_SERVICE_ID;
  if (!serviceId) {
    console.warn("VITE_APPLE_SERVICE_ID is not configured");
    return;
  }
  if (!window.AppleID) {
    console.warn("AppleID.auth JS SDK not loaded yet");
    return;
  }
  window.AppleID.auth.init({
    clientId: serviceId,
    scope: "name email",
    redirectURI: `${window.location.origin}/auth/apple/callback`,
    usePopup: true,
  });
  initialized = true;
};

export const runAppleSignIn = async (): Promise<AppleAuthResponse> => {
  if (!initialized) initAppleSignIn();
  if (!window.AppleID) {
    throw new Error("Apple Sign In is not available on this page");
  }
  return window.AppleID.auth.signIn();
};

/**
 * Apple's JS SDK rejects `signIn()` with a plain object
 * `{ error: string }` — NOT an Error instance — so `err.message`
 * is undefined and callers can't rely on it. This normalizes the
 * rejection into a stable `{ code }` so callers have one shape to
 * branch on.
 *
 * `popup_closed_by_user` / `user_cancelled_authorize` are the user
 * dismissing the sheet — callers treat those as a silent cancel,
 * everything else as a real failure worth showing + logging.
 */
export interface AppleAuthError {
  code: string;
}

const APPLE_CANCEL_CODES = new Set([
  "popup_closed_by_user",
  "user_cancelled_authorize",
  "user_trigger_new_signin_flow",
]);

export const asAppleError = (err: unknown): AppleAuthError | null => {
  if (err && typeof err === "object" && "error" in err) {
    const code = (err as { error: unknown }).error;
    if (typeof code === "string") return { code };
  }
  return null;
};

export const isAppleCancel = (err: unknown): boolean => {
  const appleErr = asAppleError(err);
  return appleErr !== null && APPLE_CANCEL_CODES.has(appleErr.code);
};
